"use client";

import * as React from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { de, enUS } from "react-day-picker/locale";
import { useTranslation } from "react-i18next";

import { cn } from "@/common/helpers/utils";
import { ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { buttonVariants } from "./button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

type CalendarDropdownProps = Parameters<
  NonNullable<NonNullable<React.ComponentProps<typeof DayPicker>["components"]>["Dropdown"]>
>[0];

function CalendarDropdown({
  options,
  value,
  onChange,
  disabled,
  className,
  "aria-label": ariaLabel,
}: CalendarDropdownProps): React.JSX.Element {
  const selectedValue = value === undefined || value === null ? undefined : String(value);

  return (
    <Select
      value={selectedValue}
      onValueChange={(nextValue) => {
        const syntheticEvent = {
          target: { value: nextValue },
          currentTarget: { value: nextValue },
        } as React.ChangeEvent<HTMLSelectElement>;

        onChange?.(syntheticEvent);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(buttonVariants({ variant: ButtonVariant.Input }), "h-8 px-3 py-1 text-sm", className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {options?.map((option) => (
          <SelectItem key={option.value} value={String(option.value)} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  fixedWeeks = true,
  navLayout = "around",
  locale,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const { i18n } = useTranslation();
  const defaultClassNames = getDefaultClassNames();
  const usesDropdownCaption = props.captionLayout?.startsWith("dropdown");
  const calendarLocale = locale ?? (i18n.resolvedLanguage?.startsWith("en") ? enUS : de);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      navLayout={navLayout}
      locale={calendarLocale}
      components={{ Dropdown: CalendarDropdown, ...components }}
      className={cn("p-3", className)}
      classNames={{
        ...defaultClassNames,
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months, classNames?.months),
        month_caption: cn(
          "relative flex min-h-9 items-center justify-center px-10",
          defaultClassNames.month_caption,
          classNames?.month_caption,
        ),
        weekdays: cn("flex flex-row", defaultClassNames.weekdays, classNames?.weekdays),
        weekday: cn("w-8 text-sm font-normal text-muted-foreground", defaultClassNames.weekday, classNames?.weekday),
        month: cn("relative w-full", defaultClassNames.month, classNames?.month),
        caption_label: cn(
          usesDropdownCaption
            ? "pointer-events-none absolute inset-0 flex items-center justify-between gap-2 px-3 text-sm font-medium"
            : "truncate text-sm font-medium",
          defaultClassNames.caption_label,
          classNames?.caption_label,
        ),
        dropdowns: cn(
          "flex w-full items-center justify-center gap-2",
          defaultClassNames.dropdowns,
          classNames?.dropdowns,
        ),
        months_dropdown: cn("w-32", defaultClassNames.months_dropdown, classNames?.months_dropdown),
        years_dropdown: cn("w-24", defaultClassNames.years_dropdown, classNames?.years_dropdown),
        chevron: cn("size-4 shrink-0 opacity-70", defaultClassNames.chevron, classNames?.chevron),
        button_next: cn(
          buttonVariants({ variant: ButtonVariant.Ghost, colorVariant: ButtonColorVariant.Secondary }),
          "absolute top-0.5 right-0 z-10 size-8 bg-transparent p-0 aria-disabled:pointer-events-none aria-disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring [&_svg]:fill-current",
          defaultClassNames.button_next,
          classNames?.button_next,
        ),
        button_previous: cn(
          buttonVariants({ variant: ButtonVariant.Ghost, colorVariant: ButtonColorVariant.Secondary }),
          "absolute top-0.5 left-0 z-10 size-8 bg-transparent p-0 aria-disabled:pointer-events-none aria-disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring [&_svg]:fill-current",
          defaultClassNames.button_previous,
          classNames?.button_previous,
        ),
        nav: cn("absolute inset-x-0 top-0 h-9", defaultClassNames.nav, classNames?.nav),
        month_grid: cn("mx-auto mt-3", defaultClassNames.month_grid, classNames?.month_grid),
        weeks: cn("flex flex-col gap-1", defaultClassNames.weeks, classNames?.weeks),
        week: cn("flex w-max items-start", defaultClassNames.week, classNames?.week),
        day: cn("flex size-8 flex-1 items-center justify-center p-0 text-sm", defaultClassNames.day, classNames?.day),
        day_button: cn(
          "size-8 cursor-pointer rounded-md p-0 font-normal transition-colors duration-150 aria-selected:opacity-100 hover:bg-accent",
          "hover:text-accent-foreground",
          defaultClassNames.day_button,
          classNames?.day_button,
        ),
        range_start: cn(
          "bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary",
          "[&>button]:hover:text-primary-foreground day-range-start rounded-s-md",
          defaultClassNames.range_start,
          classNames?.range_start,
        ),
        range_middle: cn(
          "bg-accent !text-foreground [&>button]:bg-transparent [&>button]:!text-foreground [&>button]:hover:bg-transparent",
          "[&>button]:hover:!text-foreground",
          defaultClassNames.range_middle,
          classNames?.range_middle,
        ),
        range_end: cn(
          "bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary",
          "[&>button]:hover:text-primary-foreground day-range-end rounded-e-md",
          defaultClassNames.range_end,
          classNames?.range_end,
        ),
        selected: cn(
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary",
          "[&>button]:hover:text-primary-foreground",
          defaultClassNames.selected,
          classNames?.selected,
        ),
        today: cn("[&>button]:bg-accent [&>button]:text-accent-foreground", defaultClassNames.today, classNames?.today),
        outside: cn(
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
          "aria-selected:opacity-30",
          defaultClassNames.outside,
          classNames?.outside,
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          "[&>button]:cursor-default [&>button]:hover:bg-transparent [&>button]:hover:text-muted-foreground",
          defaultClassNames.disabled,
          classNames?.disabled,
        ),
        hidden: cn("invisible flex-1", defaultClassNames.hidden, classNames?.hidden),
        ...classNames,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
